import { Component } from '@theme/component';

/**
 * @typedef {Object} UpcomingEventsRefs
 * @property {HTMLElement[]} rows
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} track
 * @property {HTMLElement} indicator
 */

/** @extends {Component<UpcomingEventsRefs>} */
export class UpcomingEventsComponent extends Component {
  requiredRefs = ['rows', 'panels'];

  #active = 0;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { rows } = this.refs;
    this.#active = Math.max(
      0,
      rows.findIndex((row) => row.classList.contains('is-active'))
    );

    this.#positionIndicator();

    this.#resizeObserver = new ResizeObserver(() => this.#positionIndicator());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Declarative handler for `on:pointerenter="/activate/{index}"` on each row.
   * @param {number} index
   */
  activate(index) {
    index = Number(index);

    const { rows, panels } = this.refs;
    if (index === this.#active || index < 0 || index >= rows.length) return;

    this.#active = index;

    for (const [i, row] of rows.entries()) {
      row.classList.toggle('is-active', i === index);
    }

    for (const [i, panel] of panels.entries()) {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('inert', !isActive);
    }

    this.#positionIndicator();
  }

  /**
   * Moves the shared accent bar to align with the active row, rather than
   * toggling a border on every row — matches the Figma spec, where a single
   * indicator slides/resizes along a track instead of each row owning one.
   */
  #positionIndicator() {
    const { track, indicator, rows } = this.refs;
    if (!track || !indicator) return;

    const row = rows[this.#active];
    if (!row) return;

    const trackRect = track.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();

    const indicatorHeight = rowRect.height / 2;
    const offset = rowRect.top - trackRect.top;

    indicator.style.transform = `translateY(${offset}px)`;
    indicator.style.height = `${indicatorHeight}px`;
  }
}

if (!customElements.get('upcoming-events-component')) {
  customElements.define('upcoming-events-component', UpcomingEventsComponent);
}
